import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  CalendarClock,
  ChevronDown,
  Clock,
  FilePlus2,
  MessageSquareWarning,
  Send,
  Share2,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { keys, reluxQuery } from '@/api/queries'
import { rpc } from '@/api/rpc'
import type { ReluxDetail } from '@/api/types'
import { can, flag, type Me } from '@/auth/me'
import { NotesField } from '@/components/form/notes-field'
import { PhotoCapture } from '@/components/media/photo-capture'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardTitle } from '@/components/ui/card'
import { queueAnswerInfo } from '@/features/relux/actions'
import { canSeePrices, EstimatesPanel } from '@/features/prices/estimates-panel'
import { ClientLinks } from '@/features/relux/view/client-link'
import { DesignView } from '@/features/relux/view/design-view'
import { FileList } from '@/features/relux/view/file-list'
import { OutcomeForm } from '@/features/relux/view/outcome-form'
import { signedUrl } from '@/lib/files'
import { formatDay, formatWhen, formatZar, plural } from '@/lib/format'
import { COMMERCIAL_STAGE, DESIGN_STATUS } from '@/lib/status'
import { db } from '@/offline/db'
import { useOnline } from '@/offline/hooks'

export const Route = createFileRoute('/_app/reluxes/$reluxId/')({
  validateSearch: (s: Record<string, unknown>): { outcome?: 'won' | 'lost' } => ({
    outcome: s.outcome === 'won' || s.outcome === 'lost' ? s.outcome : undefined,
  }),
  component: ReluxPage,
})

function ReluxPage() {
  const { reluxId } = Route.useParams()
  const { me } = Route.useRouteContext()
  const { data, error, isLoading } = useQuery(reluxQuery(reluxId))
  if (isLoading) return <p className="p-6 text-fg-muted">Loading...</p>
  if (error || !data)
    return <p className="p-6 font-semibold text-danger">{error?.message ?? 'Not found'}</p>
  return <ReluxScreen detail={data} me={me} />
}

const LIVE = new Set(['submitted', 'in_progress', 'info_needed'])

function ReluxScreen({ detail, me }: { detail: ReluxDetail; me: Me }) {
  const { relux, project } = detail
  const status = DESIGN_STATUS[relux.status]
  const live = LIVE.has(relux.status)
  const delivered = relux.status === 'completed' || relux.status === 'closed'
  const canAct = (detail.is_owner || can(me, 'projects.manage_all')) && !me.impersonating
  const { outcome } = Route.useSearch()
  const [showOutcome, setShowOutcome] = useState(!!outcome)

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <Link
          to={can(me, 'projects.read_all') ? '/queue' : '/reluxes'}
          className="font-semibold text-brand-text underline underline-offset-4"
        >
          Back
        </Link>
        <h1 className="text-3xl uppercase">{project.name}</h1>
        <p className="text-fg-muted">
          {project.ref} - Rev {relux.revision}
          {project.site_suburb && ` - ${project.site_suburb}`} - submitted{' '}
          {formatWhen(relux.submitted_at)} by {detail.created_by_name}
        </p>
        <div className="flex flex-wrap gap-2">
          <Badge tone={status.tone}>{status.label}</Badge>
          {relux.type === 'amendment' && <Badge tone="neutral">Amendment</Badge>}
          {relux.is_quick && <Badge tone="neutral">Quick</Badge>}
          {!live && relux.status !== 'cancelled' && (
            <Badge tone={COMMERCIAL_STAGE[project.commercial_stage].tone}>
              {COMMERCIAL_STAGE[project.commercial_stage].label}
            </Badge>
          )}
          {project.won_value_zar != null && (
            <Badge tone="brand">{formatZar(project.won_value_zar)}</Badge>
          )}
        </div>
      </header>

      {live && <QueueStatus detail={detail} canAct={canAct} />}
      {relux.status === 'info_needed' && <InfoNeeded detail={detail} me={me} canAct={canAct} />}

      {delivered && (
        <>
          <ShareBar detail={detail} />
          {canAct && flag(me, 'client_links') && (
            <ClientLinks reluxId={relux.id} projectName={project.name} />
          )}
          <DesignView detail={detail} />
          {canSeePrices(me) && detail.design_options.length > 0 && (
            <EstimatesPanel detail={detail} me={me} />
          )}
          {canAct && (
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="flex flex-col gap-3">
                <CardTitle>Outcome</CardTitle>
                <p className="text-fg-muted">
                  Now:{' '}
                  <strong className="text-fg">
                    {COMMERCIAL_STAGE[project.commercial_stage].label}
                  </strong>
                </p>
                <Button size="lg" onClick={() => setShowOutcome((v) => !v)}>
                  Record outcome
                </Button>
              </Card>
              <Card className="flex flex-col gap-3">
                <CardTitle>Changes needed?</CardTitle>
                <p className="text-fg-muted">Ask the designer for a new revision of this design.</p>
                <Button asChild variant="secondary" size="lg">
                  <Link to="/reluxes/$reluxId/amend" params={{ reluxId: relux.id }}>
                    <FilePlus2 aria-hidden /> Request Amendment
                  </Link>
                </Button>
              </Card>
            </div>
          )}
          {showOutcome && (
            <OutcomeForm detail={detail} userId={me.real_user_id} initial={outcome} />
          )}
        </>
      )}

      {detail.revisions.length > 1 && (
        <Card className="flex flex-col gap-3">
          <CardTitle>Revisions</CardTitle>
          <ul className="flex flex-col gap-2">
            {detail.revisions.map((r) => (
              <li key={r.id}>
                <Link
                  to="/reluxes/$reluxId"
                  params={{ reluxId: r.id }}
                  className="flex min-h-tap flex-wrap items-center gap-3 rounded-control px-2 hover:bg-surface-2"
                  aria-current={r.id === relux.id ? 'page' : undefined}
                >
                  <span className="font-semibold">Rev {r.revision}</span>
                  <Badge tone={DESIGN_STATUS[r.status].tone}>{DESIGN_STATUS[r.status].label}</Badge>
                  <span className="text-sm text-fg-muted">
                    {r.change_summary ?? formatDay(r.submitted_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <WhatWasSent detail={detail} />
    </div>
  )
}

function QueueStatus({ detail, canAct }: { detail: ReluxDetail; canAct: boolean }) {
  const { relux } = detail
  const queryClient = useQueryClient()
  const online = useOnline()
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.relux(relux.id) })
    void queryClient.invalidateQueries({ queryKey: ['queue'] })
    void queryClient.invalidateQueries({ queryKey: ['reluxes'] })
  }
  const requestEta = useMutation({
    mutationFn: () => rpc('request_eta', { rid: relux.id }),
    onSuccess: refresh,
  })
  const cancel = useMutation({
    mutationFn: () => rpc('cancel_relux', { rid: relux.id, reason }),
    onSuccess: refresh,
  })
  const [openedAt] = useState(() => Date.now())
  const askedRecently =
    relux.eta_requested_at && openedAt - new Date(relux.eta_requested_at).getTime() < 24 * 3600_000

  return (
    <Card className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-sm font-semibold text-fg-muted uppercase">Queue</p>
          <p className="font-display text-4xl">#{detail.queue_position}</p>
          <p className="text-fg-muted">
            {detail.ahead_count === 0
              ? 'Next up'
              : `${plural(detail.ahead_count, 'Relux', 'Reluxes')} ahead of yours`}
          </p>
        </div>
        <div>
          <p className="text-sm font-semibold text-fg-muted uppercase">ETA</p>
          <p className="font-display text-4xl">{relux.eta ? formatDay(relux.eta) : 'Not set'}</p>
          {relux.status === 'in_progress' && (
            <p className="text-fg-muted">The designer is working on it.</p>
          )}
        </div>
      </div>
      {canAct && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="lg"
            disabled={!online || !!askedRecently || requestEta.isPending}
            onClick={() => requestEta.mutate()}
          >
            <CalendarClock aria-hidden />{' '}
            {askedRecently
              ? `You asked ${formatWhen(relux.eta_requested_at)}`
              : 'Ask the designer for an ETA'}
          </Button>
          {relux.status === 'submitted' && !relux.started_at && !cancelling && (
            <Button
              variant="ghost"
              size="lg"
              className="text-danger"
              onClick={() => setCancelling(true)}
            >
              <X aria-hidden /> Cancel this Relux
            </Button>
          )}
        </div>
      )}
      {requestEta.isSuccess && (
        <p role="status" className="font-semibold text-success-fg">
          The designer has been asked for an ETA.
        </p>
      )}
      {(requestEta.error || cancel.error) && (
        <p role="alert" className="font-semibold text-danger">
          {(requestEta.error ?? cancel.error)?.message}
        </p>
      )}
      {cancelling && (
        <div className="flex flex-col gap-3 rounded-control bg-danger-bg p-4">
          <NotesField
            label="Why are you cancelling? (optional)"
            value={reason}
            onChange={setReason}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              disabled={!online || cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              Cancel Relux
            </Button>
            <Button variant="outline" onClick={() => setCancelling(false)}>
              Keep it
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

function InfoNeeded({ detail, me, canAct }: { detail: ReluxDetail; me: Me; canAct: boolean }) {
  const { relux } = detail
  const [message, setMessage] = useState('')
  const [photos, setPhotos] = useState<string[]>([])
  const [sent, setSent] = useState(false)
  const send = async () => {
    await queueAnswerInfo(
      { reluxId: relux.id, userId: me.real_user_id, message, fileIds: photos, categories: {} },
      detail.project.name,
    )
    setSent(true)
  }
  return (
    <Card className="flex flex-col gap-4 border-2 border-danger">
      <CardTitle className="flex items-center gap-2">
        <MessageSquareWarning className="size-6 text-danger" aria-hidden /> The designer needs more
        info
      </CardTitle>
      {relux.info_needed_message && <p className="text-lg">{relux.info_needed_message}</p>}
      {relux.info_needed_checklist.length > 0 && (
        <ul className="list-disc pl-6">
          {relux.info_needed_checklist.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
      {canAct &&
        (sent ? (
          <p role="status" className="font-semibold text-success-fg">
            Reply saved. It goes to the designer as soon as you have signal.
          </p>
        ) : (
          <>
            <NotesField label="Your reply" value={message} onChange={setMessage} />
            <PhotoCapture
              userId={me.real_user_id}
              label="Add photos (optional)"
              fileIds={photos}
              onAdd={(ids) => setPhotos((p) => [...p, ...ids])}
              onRemove={(id) => {
                void db.files.delete(id)
                setPhotos((p) => p.filter((x) => x !== id))
              }}
            />
            <Button
              size="lg"
              onClick={() => void send()}
              disabled={!message.trim() && photos.length === 0}
            >
              <Send aria-hidden /> Send to the designer
            </Button>
          </>
        ))}
    </Card>
  )
}

/** Native share sheet (WhatsApp, email...) with the design files attached where supported. */
function ShareBar({ detail }: { detail: ReluxDetail }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const files = detail.files.filter((f) => f.kind === 'deliverable' && f.client_facing)
  const share = async () => {
    setMsg(null)
    setBusy(true)
    try {
      const blobs = await Promise.all(
        files.map(async (f) => {
          const res = await fetch(await signedUrl(f.storage_path))
          return new File([await res.blob()], f.original_name, {
            type: f.mime ?? 'application/octet-stream',
          })
        }),
      )
      const data = {
        title: `${detail.project.name} - lighting design`,
        text: `Lighting design for ${detail.project.name} (${detail.project.ref} Rev ${detail.relux.revision}) from Mesmerize Lighting.`,
        files: blobs,
      }
      if (navigator.canShare?.(data)) await navigator.share(data)
      else setMsg('This device cannot share files directly. Use Download on each file below.')
    } catch (e) {
      if ((e as Error).name !== 'AbortError')
        setMsg('Sharing needs signal. Try again when you are connected.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex flex-col gap-2">
      <Button size="lg" onClick={() => void share()} disabled={busy || files.length === 0}>
        <Share2 aria-hidden /> {busy ? 'Preparing...' : 'Share with client'}
      </Button>
      {msg && <p className="text-fg-muted">{msg}</p>}
    </div>
  )
}

function WhatWasSent({ detail }: { detail: ReluxDetail }) {
  const [open, setOpen] = useState(false)
  const inputs = detail.files.filter((f) => f.kind === 'input')
  return (
    <Card className="p-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-tap-lg w-full items-center justify-between px-5 text-left"
      >
        <span className="text-lg font-semibold uppercase">What was sent to the designer</span>
        <ChevronDown className={open ? 'size-6 rotate-180' : 'size-6'} aria-hidden />
      </button>
      {open && (
        <div className="flex flex-col gap-4 border-t border-border p-5">
          <p className="flex items-center gap-2 text-fg-muted">
            <Clock className="size-4" aria-hidden /> {detail.relux.completeness_score}% complete
            when sent
          </p>
          {detail.relux.change_summary && (
            <p>
              <strong>Changes:</strong> {detail.relux.change_summary}
            </p>
          )}
          {detail.spaces.map((s) => (
            <p key={s.id}>
              <strong>{s.name}</strong>{' '}
              {[
                s.length_m && s.width_m && `${s.length_m} x ${s.width_m} m`,
                s.height_m && `${s.height_m} m high`,
                s.target_lux && `${s.target_lux} lux`,
                s.lux_undecided && 'lux: designer to decide',
              ]
                .filter(Boolean)
                .join(' - ')}
            </p>
          ))}
          {detail.contacts.map((c) => (
            <p key={c.id}>{[c.full_name, c.company, c.role].filter(Boolean).join(' - ')}</p>
          ))}
          {detail.relux.notes && <p className="whitespace-pre-line">{detail.relux.notes}</p>}
          <FileList files={inputs} />
        </div>
      )}
    </Card>
  )
}
